import { useState, useCallback } from 'react';
import { progressTracker, adaptiveSessionStateManager } from '@services';
import { isFillInAnswerCorrect } from '@utils/fillInAnswerMatching';

/**
 * Hook for submitting answers and updating progress
 * Integrates with adaptive session state for:
 * - Recording recent answer history for performance calculation
 * - Tracking consecutive misses for remediation triggers
 * - Updating dynamic question group sizing
 */
export function useAnswerSubmission(userId, courseId, sessionId = null) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [recentPerformance, setRecentPerformance] = useState(null);

  const submitAnswer = useCallback(async (questionId, answerData, questionTags = []) => {
    try {
      setSubmitting(true);
      setError(null);

      // Record the answer in standard progress tracking
      const result = await progressTracker.recordUserAnswer(
        userId,
        courseId,
        questionId,
        answerData
      );

      // If we have a session, also update adaptive session state
      if (sessionId) {
        try {
          const isCorrect = result?.isCorrect || answerData?.isCorrect || false;
          
          // Record the answer in recent history for performance window
          await adaptiveSessionStateManager.recordRecentAnswer(
            sessionId,
            questionId,
            questionTags,
            isCorrect
          );

          // Update consecutive misses for remediation tracking
          for (const tag of (questionTags || [])) {
            await adaptiveSessionStateManager.updateConsecutiveMisses(
              sessionId,
              tag,
              isCorrect
            );
          }

          // Calculate and update group sizing based on recent performance
          const performance = await adaptiveSessionStateManager.calculateRecentPerformance(sessionId);
          setRecentPerformance(performance);

          // Update the session's next question group size
          await adaptiveSessionStateManager.updateQuestionGroupSize(sessionId);

          console.log('[useAnswerSubmission] Adaptive session state updated:', {
            sessionId,
            questionId,
            isCorrect,
            performance: {
              correctRate: (performance.correctRate * 100).toFixed(1) + '%',
              recommendation: performance.recommendation,
              sampleSize: performance.sampleSize,
            },
          });
        } catch (stateErr) {
          console.warn('[useAnswerSubmission] Error updating session state:', stateErr);
          // Don't fail the main flow on session state error
        }
      }

      setLastResult(result);
      return result;
    } catch (err) {
      console.error('Error submitting answer:', err);
      setError(err.message);
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [userId, courseId, sessionId]);

  const clearResult = useCallback(() => {
    setLastResult(null);
    setRecentPerformance(null);
    setError(null);
  }, []);

  return {
    submitAnswer,
    submitting,
    error,
    lastResult,
    recentPerformance,
    clearResult,
  };
}

/**
 * Hook for checking answer correctness (client-side validation)
 */
export function useAnswerValidation() {
  const validateAnswer = useCallback((question, userAnswer) => {
    if (!question || userAnswer === undefined || userAnswer === null) {
      return null;
    }

    switch (question.type) {
      case 'multiple-choice':
        return userAnswer === question.correctAnswer;
      
      case 'fill-in': {
        return isFillInAnswerCorrect(userAnswer, question.correctAnswer);
      }
      
      case 'code':
        // Code validation would need to run test cases
        // This is a placeholder - actual implementation would require backend
        return null;
      
      default:
        return null;
    }
  }, []);

  return { validateAnswer };
}
